<!-- migration-form.component.html -->
<div class="dialog-container">
  <!-- Dialog Header -->
  <div class="dialog-header">
    <h4 class="modal-title synchpc-primary" id="modal-title">
      Add Local Repository
    </h4>
    <i
      style="z-index: 1"
      class="bi bi-x-lg text-danger"
      color="warn"
      (click)="onClose()"
      title="Close"
    ></i>
  </div>

  <!-- Dialog Content -->
  <div mat-dialog-content class="dialog-content">
    <form [formGroup]="migrationForm" class="migration-form">
      <!-- Basic Information -->
      <div class="form-section">
        <h3>Repository Information</h3>

        <div class="form-group">
          <label for="huggingFaceUrl">HuggingFace Repository URL *</label>
          <input
            type="url"
            id="huggingFaceUrl"
            formControlName="huggingFaceUrl"
            placeholder="https://huggingface.co/microsoft/DialoGPT-medium"
            class="form-control"
            [class.is-invalid]="isFieldInvalid('huggingFaceUrl')"
          />
          <div
            class="invalid-feedback"
            *ngIf="isFieldInvalid('huggingFaceUrl')"
          >
            {{ getFieldError("huggingFaceUrl") }}
          </div>
        </div>

        <div class="form-row">
          <div class="form-group col-md-8">
            <label for="repoName">Repository Name *</label>
            <input
              type="text"
              id="repoName"
              formControlName="repoName"
              placeholder="my-model"
              class="form-control"
              [class.is-invalid]="isFieldInvalid('repoName')"
            />
            <div class="invalid-feedback" *ngIf="isFieldInvalid('repoName')">
              {{ getFieldError("repoName") }}
            </div>
          </div>

          <div class="form-group col-md-4">
            <label for="repoOwner">Owner *</label>
            <input
              type="text"
              id="repoOwner"
              formControlName="repoOwner"
              placeholder="username"
              class="form-control"
              [class.is-invalid]="isFieldInvalid('repoOwner')"
            />
            <div class="invalid-feedback" *ngIf="isFieldInvalid('repoOwner')">
              {{ getFieldError("repoOwner") }}
            </div>
          </div>
        </div>

        <div class="form-group">
          <label for="description">Description</label>
          <textarea
            id="description"
            formControlName="description"
            placeholder="Optional description for the migrated repository"
            class="form-control"
            rows="3"
          ></textarea>
        </div>

        <div class="form-check">
          <input
            type="checkbox"
            id="isPrivate"
            formControlName="isPrivate"
            class="form-check-input"
          />
          <label class="form-check-label" for="isPrivate">
            Create as private repository
          </label>
        </div>
      </div>

      <!-- Authentication Section -->
      <div class="form-section">
        <h3>Authentication</h3>
        <p class="text-muted">
          Required for private repositories or to avoid rate limiting
        </p>

        <div class="form-check">
          <input
            type="checkbox"
            id="needsAuth"
            formControlName="needsAuth"
            class="form-check-input"
          />
          <label class="form-check-label" for="needsAuth">
            Repository requires authentication
          </label>
        </div>

        <div *ngIf="migrationForm.get('needsAuth')?.value" class="auth-fields">
          <div class="alert alert-info">
            <strong>How to get HuggingFace Access Token:</strong>
            <ol>
              <li>
                Go to
                <a href="https://huggingface.co/settings/tokens" target="_blank"
                  >HuggingFace Tokens</a
                >
              </li>
              <li>Click "New token"</li>
              <li>Choose "Read" access for repository access</li>
              <li>Copy the token (starts with "hf_")</li>
            </ol>
          </div>

          <div class="form-group">
            <label for="hfUsername">HuggingFace Username *</label>
            <input
              type="text"
              id="hfUsername"
              formControlName="hfUsername"
              placeholder="your-hf-username"
              class="form-control"
              [class.is-invalid]="isFieldInvalid('hfUsername')"
            />
            <div class="invalid-feedback" *ngIf="isFieldInvalid('hfUsername')">
              {{ getFieldError("hfUsername") }}
            </div>
          </div>

          <div class="form-group">
            <label for="hfAccessToken">HuggingFace Access Token *</label>
            <input
              type="password"
              id="hfAccessToken"
              formControlName="hfAccessToken"
              placeholder="hf_xxxxxxxxxxxxxxxxxxxx"
              class="form-control"
              [class.is-invalid]="isFieldInvalid('hfAccessToken')"
            />
            <small class="form-text text-muted"
              >Token should start with "hf_"</small
            >
            <div
              class="invalid-feedback"
              *ngIf="isFieldInvalid('hfAccessToken')"
            >
              {{ getFieldError("hfAccessToken") }}
            </div>
          </div>
        </div>
      </div>

      <!-- Advanced Options -->
      <div class="form-section">
        <div class="form-check">
          <input
            type="checkbox"
            id="showAdvanced"
            [(ngModel)]="showAdvanced"
            [ngModelOptions]="{ standalone: true }"
            class="form-check-input"
          />
          <label class="form-check-label" for="showAdvanced">
            Show advanced options
          </label>
        </div>

        <div *ngIf="showAdvanced" class="advanced-options">
          <h3>Advanced Options</h3>

          <div class="form-check">
            <input
              type="checkbox"
              id="enableWiki"
              formControlName="enableWiki"
              class="form-check-input"
            />
            <label class="form-check-label" for="enableWiki">
              Enable Wiki
            </label>
          </div>

          <div class="form-check">
            <input
              type="checkbox"
              id="enableReleases"
              formControlName="enableReleases"
              class="form-check-input"
            />
            <label class="form-check-label" for="enableReleases">
              Enable Releases
            </label>
          </div>

          <div class="form-check">
            <input
              type="checkbox"
              id="enableLFS"
              formControlName="enableLFS"
              class="form-check-input"
            />
            <label class="form-check-label" for="enableLFS">
              Enable Git LFS (for large files)
            </label>
          </div>

          <div class="form-check">
            <input
              type="checkbox"
              id="createAsMirror"
              formControlName="createAsMirror"
              class="form-check-input"
            />
            <label class="form-check-label" for="createAsMirror">
              Create as mirror (keeps in sync with source)
            </label>
          </div>
        </div>
      </div>

      <!-- Migration Result -->
      <div *ngIf="migrationResult" class="migration-result mt-4">
        <div *ngIf="migrationResult.success" class="alert alert-success">
          <h4>✅ Migration Successful!</h4>
          <p>{{ migrationResult.message }}</p>
          <p *ngIf="migrationResult.repository_url">
            <strong>Repository URL:</strong>
            <a [href]="migrationResult.repository_url" target="_blank">
              {{ migrationResult.repository_url }}
            </a>
          </p>
        </div>

        <div *ngIf="!migrationResult.success" class="alert alert-danger">
          <h4>❌ Migration Failed</h4>
          <p>{{ migrationResult.message }}</p>
          <p *ngIf="migrationResult.error" class="small">
            <strong>Error details:</strong> {{ migrationResult.error }}
          </p>
        </div>
      </div>
    </form>
  </div>

  <!-- Dialog Actions -->
  <div mat-dialog-actions class="dialog-actions">
    <!-- <button
      mat-button
      type="button"
      (click)="onCancel()"
      [disabled]="isLoading"
      class="cancel-button"
    >
      Cancel
    </button>
    <button
      mat-raised-button
      color="primary"
      type="button"
      (click)="onSubmit()"
      [disabled]="isLoading || migrationForm.invalid"
      class="submit-button"
    >
      <mat-spinner *ngIf="isLoading" [diameter]="20" class="me-2"></mat-spinner>
      {{ isLoading ? "Migrating..." : "Start Migration" }}
    </button> -->
    <button type="button" class="btn btn-outline-secondary" (click)="onClose()">
      Close
    </button>
    <button type="submit" class="btn btn-primary" (click)="onSubmit()">
      Submit
    </button>
  </div>
</div>
